import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, AtSign } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1512100356356-de1b84283e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwdHJhdmVsJTIwY2x1YnxlbnwwfHx8fDE3ODM2NDMyMjF8MA&ixlib=rb-4.1.0&q=85";

const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState("checking"); // checking | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const run = async () => {
      if (!token) { setState("error"); setMessage("This confirmation link is missing its token."); return; }
      try {
        const { data } = await api.post("/profile/verify-email-change", { token });
        setState("success");
        setMessage(data.message || "Your email address has been updated.");
      } catch (err) {
        setState("error");
        setMessage(formatApiErrorDetail(err.response?.data?.detail) || "This confirmation link is invalid or has expired.");
      }
    };
    run();
  }, [token]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ir-bg)]" data-testid="verify-email-change-page">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ir-blue-dark)]/70 via-[var(--ir-blue-dark)]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Confirm your new email<br /><span className="text-gradient-orange italic">and stay connected.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/"><Logo size="md" /></Link>

          {state === "checking" && (
            <div className="mt-12 flex items-center gap-3 text-[var(--ir-muted)]" data-testid="verify-email-checking">
              <Loader2 className="w-5 h-5 animate-spin" /> Confirming your new email address…
            </div>
          )}

          {state === "success" && (
            <div className="mt-10" data-testid="verify-email-success">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-[var(--ir-orange)]" />
                <h1 className="font-display text-3xl text-[var(--ir-text)]">Email updated</h1>
              </div>
              <p className="text-[var(--ir-muted)] mt-4 leading-relaxed">{message}</p>
              <Button asChild className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8" data-testid="verify-email-login-btn">
                <Link to="/login"><AtSign className="w-4 h-4 mr-2" />Sign in with your new email</Link>
              </Button>
            </div>
          )}

          {state === "error" && (
            <div className="mt-10" data-testid="verify-email-error">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-[var(--ir-orange)]" />
                <h1 className="font-display text-3xl text-[var(--ir-text)]">Link not valid</h1>
              </div>
              <p className="text-[var(--ir-muted)] mt-4 leading-relaxed">{message} You can request a new change from your profile after signing in.</p>
              <Button asChild className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8" data-testid="verify-email-back-btn">
                <Link to="/dashboard?tab=profile">Go to my profile</Link>
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default VerifyEmailChange;
